import React from "react";
import { Input, Select, DatePicker, Icon } from "@amzn/awsui-components-react";
import { TaskSuggestion } from "./definitions";
import { GaiaConstants } from "../../utils/gaia-constants";
import {
  formatDateValue,
  toUnixEpochTime,
  normalizeDateFormat,
} from "../../utils/date-format";

const createBaseEditConfig = (ariaLabel: string) => ({
  ariaLabel,
  editIconAriaLabel: "editable",
  errorIconAriaLabel: `${ariaLabel} Error`,
});

const createInputColumn = (
  id: keyof TaskSuggestion,
  width: number,
  header: string,
  cellRenderer?: (item: TaskSuggestion) => React.ReactElement | string
) => ({
  id,
  width,
  header,
  cell: cellRenderer || ((item: TaskSuggestion) => item[id] || ""),
  sortingField: id,
  editConfig: {
    ...createBaseEditConfig(header),
    editingCell: (item: TaskSuggestion, { currentValue, setValue }: any) => {
      const value = currentValue ?? item[id];
      return React.createElement(Input, {
        autoFocus: true,
        value: value,
        onChange: ({ detail }: any) => setValue(detail.value),
      });
    },
  },
});

const createSelectColumn = (
  id: keyof TaskSuggestion,
  width: number,
  header: string,
  optionsKey: string,
  uiContent: any
) => ({
  id,
  width,
  header,
  cell: (item: TaskSuggestion) => item[id] || "",
  editConfig: {
    ...createBaseEditConfig(header),
    editingCell: (item: TaskSuggestion, { currentValue, setValue }: any) => {
      const value = currentValue ?? item[id];
      return React.createElement(Select, {
        autoFocus: true,
        expandToViewport: true,
        selectedOption:
          uiContent[optionsKey]?.find((opt: any) => opt.value === value) ||
          null,
        onChange: ({ detail }: any) =>
          setValue(detail.selectedOption.value || value),
        options: uiContent[optionsKey] || [],
        filteringType: "auto",
      });
    },
  },
});

const createDatePickerColumn = (
  id: keyof TaskSuggestion,
  width: number,
  header: string
) => ({
  id,
  width,
  header,
  cell: (item: TaskSuggestion) =>
    item[id] && typeof item[id] === "string"
      ? formatDateValue(
          toUnixEpochTime(normalizeDateFormat(item[id] as string))
        )
      : "",
  editConfig: {
    ...createBaseEditConfig(header),
    editingCell: (item: TaskSuggestion, { currentValue, setValue }: any) => {
      const value = currentValue ?? item[id];
      return React.createElement(DatePicker, {
        autoFocus: true,
        expandToViewport: true,
        onChange: ({ detail }: any) => setValue(detail.value),
        value: value,
        placeholder: "YYYY/MM/DD",
      });
    },
  },
});

export const createTaskTableColumns = (uiContent: any, teamName?: string) => {
  const showDescriptionColumn = teamName === GaiaConstants.INDIA_DIRECT_TEAM;

  const nameColumn = {
    ...createInputColumn("name", 280, "Name", (item: TaskSuggestion) =>
      React.createElement(
        "div",
        { style: { whiteSpace: "pre-wrap" } },
        item.milestone
          ? React.createElement(Icon, { name: "flag", variant: "link" })
          : null,
        item.milestone ? " " : null,
        item.name
      )
    ),
  };

  const descriptionColumn = {
    ...createInputColumn(
      "description",
      400,
      "Description",
      (item: TaskSuggestion) => {
        const description = item.description || "";
        if (description.includes("**Verbatim:**")) {
          const parts = description.split("\n\n**Verbatim:** ");
          if (parts.length === 2) {
            return React.createElement(
              "div",
              { style: { whiteSpace: "pre-wrap" } },
              parts[0],
              React.createElement("br"),
              React.createElement("br"),
              React.createElement("strong", null, "Verbatim:"),
              " ",
              parts[1]
            );
          }
        }
        const verbatimIndex = description.toLowerCase().indexOf("verbatim:");
        if (verbatimIndex !== -1) {
          const beforeVerbatim = description.substring(0, verbatimIndex).trim();
          const afterVerbatim = description.substring(verbatimIndex + 9).trim();
          return React.createElement(
            "div",
            { style: { whiteSpace: "pre-wrap" } },
            beforeVerbatim,
            beforeVerbatim ? React.createElement("br") : null,
            beforeVerbatim ? React.createElement("br") : null,
            React.createElement("strong", null, "Verbatim:"),
            " ",
            afterVerbatim
          );
        }
        return React.createElement(
          "div",
          { style: { whiteSpace: "pre-wrap" } },
          description
        );
      }
    ),
  };

  const columns = [
    nameColumn,
    descriptionColumn,
    createSelectColumn("type", 220, "Type", "inquiryTypeOptions", uiContent),
    createSelectColumn("status", 220, "Status", "taskStatusOptions", uiContent),
    createDatePickerColumn("dueDate", 220, "Due date"),
    createSelectColumn("owners", 220, "Owner", "ownerOptions", uiContent),
    createDatePickerColumn("plannedStart", 220, "Planned start"),
    createSelectColumn(
      "complexity",
      160,
      "Complexity",
      "complexityOptions",
      uiContent
    ),
    createSelectColumn(
      "priority",
      160,
      "Priority",
      "priorityOptions",
      uiContent
    ),
  ];
  if (showDescriptionColumn) {
    return columns;
  } else {
    return columns.filter((column) => column.id !== "description");
  }
};
